<template>
    <div
        class="position-relative"
        @keyup.esc="close"
    >
        <input-text
            ref="select"
            :status="status"
            :name="name"
            :placeholder="placeholder"
            :inner-label="innerLabel"
            :disabled="disabled"
            :value="text || query"
            :readonly="!!value"
            @update:value="search"
            @focus="$emit('focus')"
            @blur="$emit('blur')"
            @keyup.tab="focusOnList"
            @keyup.down.stop.prevent="focusOnList"
        >
            <template #left>
                <slot name="left" />
            </template>

            <template #right>
                <button-link
                    v-if="!disabled && (text || query)"
                    theme="gray-dark"
                    icon="close"
                    :icon-size="20"
                    @click.prevent="erase"
                />
            </template>
        </input-text>

        <transition
            name="fade"
            class="w-100 overflow-hidden position-fixed"
            @hide.stop="close"
        >
            <remote-list
                v-show="opened"
                ref="list"
                class="bg-light position-absolute w-100 z-index--1 rounded-bottom shadow--bottom"
                :value="value"
                :options="options"
                :loading="loading"
                @update:value="change"
            />
        </transition>
    </div>
</template>

<script>
import debounce from "lodash/debounce"

import ButtonLink from "~/components/buttons/ButtonLink"
import InputStatus from "~/components/inputs/InputStatus"
import InputText from "~/components/inputs/InputText"
import RemoteList from "~/components/RemoteList"

export default {
    props: {
        ...InputStatus.props,

        placeholder: {
            type: String,
            default: null,
        },

        innerLabel: {
            type: String,
            default: null,
        },

        name: {
            type: String,
            default: null,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        value: {
            type: [Array, String, Number],
            default: null,
        },

        endpoint: {
            type: String,
            default: null,
        },

        nameKey: {
            type: String,
            default: "name",
        },

        debounceWait: {
            type: Number,
            default: 300,
        },
    },

    emits: ["update:value", "focus", "blur"],

    data() {
        return {
            opened: false,
            query: "",
            options: [],
            loading: false,
            text: "",
        }
    },

    watch: {
        async value() {
            this.text = await this.getText()
        },
    },

    async created() {
        this.text = await this.getText()
        this.updateOptions = debounce(this.updateOptions, this.debounceWait, {
            leading: true,
        })
        this.getText = debounce(this.getText, this.debounceWait, {
            leading: true,
        })
    },

    methods: {
        close() {
            if (!this.disabled) {
                this.opened = false
            }
        },

        open() {
            if (!this.disabled) {
                this.opened = true
            }
        },

        change(val) {
            if (this.disabled) return
            this.query = val.text
            this.text = val.text
            this.$emit("update:value", val.value)
            this.close()
        },

        async getText() {
            if (!this.value) return ""
            try {
                const item = await this.$http.fetchData(
                    `${this.endpoint}/${this.value}`
                )
                return item[this.nameKey]
            } catch (_) {
                return ""
            }
        },

        focusOnList() {
            if (!this.opened) return
            const list = this.$refs.list.$el
            list.focus()
        },

        erase() {
            this.query = ""
            this.close()
            this.$emit("update:value", null)
        },

        async updateOptions() {
            this.loading = true
            try {
                const url = `${this.endpoint}?filter=free_search:${this.query}&limit=5`
                const rowOptions = await this.$http.fetchData(url)

                if (rowOptions.length == 0) {
                    this.loading = false
                    this.options = []
                    return
                }
                const key = rowOptions[0]._meta.key
                this.options = rowOptions.map(opt => {
                    return {
                        text: opt[this.nameKey],
                        value: opt[key],
                    }
                })
            } catch (e) {
                console.log(e)
            } finally {
                this.open()
                this.loading = false
            }
        },

        async search(query) {
            this.query = query
            if (query.length < 3) {
                this.close()
                return
            }
            try {
                await this.updateOptions()
            } finally {
                this.open()
            }
        },
    },

    components: {
        ButtonLink,
        InputText,
        RemoteList,
    },
}
</script>
